import * as React from "react"
import { gql } from "@apollo/client"
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Image,
  LinkBox,
  LinkOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import { nestedPaginate } from "lib/apollo/helpers"
import { ProfileEventFragment, useGetProfileEventsQuery } from "lib/graphql"

import { transformImage } from "../lib/helpers/utils"
import { LinkButton } from "./LinkButton"

dayjs.extend(utc)

const _ = gql`
  fragment ProfileEvent on Event {
    id
    name
    slug
    date
    time
    location
    image
    type
    region
    categories
  }

  query GetProfileEvents(
    $id: String!
    $take: Int
    $orderBy: [UserEventOrderByWithRelationInput!]
    $where: UserEventWhereInput
    $skip: Int
  ) {
    user(id: $id) {
      id
      events(take: $take, orderBy: $orderBy, where: $where, skip: $skip) {
        items {
          ...ProfileEvent
        }
        count
      }
    }
  }
`

interface Props {
  userId: string
}

const TAKE = 6

export function ProfileEvents({ userId }: Props) {
  const {
    data: futureEventsData,
    loading: futureEventsLoading,
    fetchMore: futureEventsFetchMore,
  } = useGetProfileEventsQuery({
    variables: {
      id: userId,
      take: TAKE,
      where: { event: { is: { date: { gte: dayjs.utc().format("YYYY-MM-DD") } } } },
    },
  })

  const futureEvents = futureEventsData?.user?.events?.items
  const futureEventsCount = futureEventsData?.user?.events?.count

  const {
    data: pastEventsData,
    loading: pastEventsLoading,
    fetchMore: pastEventsFetchMore,
  } = useGetProfileEventsQuery({
    variables: {
      id: userId,
      take: TAKE,
      where: { event: { is: { date: { lt: dayjs.utc().format("YYYY-MM-DD") } } } },
    },
  })

  const pastEvents = pastEventsData?.user?.events?.items
  const pastEventsCount = pastEventsData?.user?.events?.count

  const handleFutureEventsFetchMore = () => {
    if (!futureEvents) return
    return futureEventsFetchMore({
      variables: { skip: futureEvents.length, take: TAKE },
      updateQuery: nestedPaginate("user", "events"),
    })
  }

  const handlePastEventsFetchMore = () => {
    if (!pastEvents) return
    return pastEventsFetchMore({
      variables: { skip: pastEvents.length, take: TAKE },
      updateQuery: nestedPaginate("user", "events"),
    })
  }

  if (futureEventsLoading || pastEventsLoading)
    return (
      <Center>
        <Spinner />
      </Center>
    )
  if (!futureEvents || !pastEvents) return null

  return (
    <Box>
      {futureEvents.length > 0 ? (
        <>
          <Flex flexDir="row" alignItems="center" mt={10} mb={6}>
            <Heading color="gray.400" fontSize="2xl">
              Aankomend
            </Heading>
          </Flex>

          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={5}>
            {futureEvents.map((event: ProfileEventFragment, i) => (
              <ProfileEventTile key={i} event={event} />
            ))}
          </SimpleGrid>
          {futureEventsCount && futureEvents.length < futureEventsCount && (
            <Center w="100%" mt={4}>
              <Button
                type="button"
                variant="outline"
                onClick={handleFutureEventsFetchMore}
                colorScheme="gray"
                size="sm"
              >
                Meer laden
              </Button>
            </Center>
          )}
        </>
      ) : (
        <Stack mt={8} spacing={8} align="center">
          <Text color="gray.500"> Nog geen aankomende agenda items</Text>
        </Stack>
      )}
      {pastEvents.length > 0 && (
        <>
          <Flex flexDir="row" alignItems="center" mt={10} mb={6}>
            <Heading color="gray.400" fontSize="2xl">
              Verleden
            </Heading>
          </Flex>
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={5}>
            {pastEvents.map((event: ProfileEventFragment, i) => (
              <ProfileEventTile key={i} event={event} />
            ))}
          </SimpleGrid>
          {pastEventsCount && pastEvents.length < pastEventsCount && (
            <Center w="100%" mt={4}>
              <Button
                type="button"
                variant="outline"
                onClick={handlePastEventsFetchMore}
                colorScheme="gray"
                size="sm"
              >
                Meer laden
              </Button>
            </Center>
          )}
        </>
      )}
      <Stack align="center" pt={10}>
        <LinkButton href="/te-doen" size="md" variant="solid" colorScheme="yellow">
          Vind agenda items
        </LinkButton>
      </Stack>
    </Box>
  )
}

interface ProfileEventTileProps {
  event: ProfileEventFragment
}

const ProfileEventTile: React.FC<ProfileEventTileProps> = ({ event }) => {
  return (
    <LinkBox position="relative" overflow="hidden">
      <LinkOverlay href={`/te-doen/${event.slug}`} textDecoration="none">
        <Image
          src={event?.image ? transformImage(event?.image, "h_350") : "/project-placeholder.png"}
          h="210px"
          w="100%"
          objectFit="cover"
          objectPosition="center"
          alt="evenement afbeelding"
          borderRadius="md"
        />
        <Box width="100%" py={3} px={1}>
          <Text fontWeight="bold" fontSize="2xl" textTransform="capitalize">
            {event.name}
          </Text>
          <Text fontSize={16} color="gray.500" whiteSpace="nowrap" overflow="hidden" isTruncated>
            {event.type && `${event.type} / `} {event.region && `${event.region} / `}
            {event.categories?.reduce((acc, i) => acc + i + ", ", "").slice(0, -2)}
          </Text>

          <Text fontWeight="bold" fontSize={16} color="yellow.500">
            {event.date && dayjs.utc(event.date).format("DD MMM")}
            {event.time && ` @ ${event.time}`}
          </Text>
        </Box>
      </LinkOverlay>
    </LinkBox>
  )
}
